/**
 * Profils datés (PRD §3 « Effets datés »).
 * - scalaire : valeur en régime, multipliée par la montée en charge linéaire `rampFactor` ;
 * - tableau : valeur de l'année k depuis l'adoption (index 0 = première année), la dernière valeur persiste
 *   (mesures) ou tombe à zéro après la fin du tableau (événements, `persistLast = false`).
 */
import type { HypTarget, Measure, Profile } from "./types.js";

/** Part de la mesure en vigueur l'année k (0-based) pour une montée en charge de `ramp` années ; ramp ≤ 1 → plein effet dès l'année 1. */
export function rampFactor(k: number, ramp: number): number {
  if (k < 0) return 0;
  if (ramp <= 1) return 1;
  return Math.min(1, (k + 1) / ramp);
}

/** Valeur effective d'un profil l'année k (0-based) depuis l'adoption. */
export function profileAt(p: Profile, k: number, ramp: number, persistLast = true): number {
  if (k < 0) return 0;
  if (Array.isArray(p)) {
    if (k < p.length) return p[k] ?? 0;
    return persistLast ? (p[p.length - 1] ?? 0) : 0;
  }
  return p * rampFactor(k, ramp);
}

/** Valeur en régime (dernière année du profil, ou scalaire). */
export function profileSteady(p: Profile): number {
  return Array.isArray(p) ? (p[p.length - 1] ?? 0) : p;
}

/** Valeur la première année (profil[0] ou scalaire × montée en charge de l'année 1). */
export function profileFirst(p: Profile, ramp: number): number {
  return profileAt(p, 0, ramp);
}

/**
 * Cible du curseur d'hypothèse d'une mesure (ADR 0004) : `hyp.applies_to`, défaut `cost_leak`.
 * Sans `hyp`, le curseur n'existe pas et la fonction renvoie `undefined`.
 */
export function hypTarget(m: Pick<Measure, "hyp">): HypTarget | undefined {
  if (!m.hyp) return undefined;
  return m.hyp.applies_to ?? "cost_leak";
}
